import React from 'react';

import styled from 'styled-components';

import { NwLinkButton } from '@components/Gui/NwButton';
import NwDropDownMenu from '@components/Gui/NwDropDownMenu';
import NwIcon from '@components/Gui/NwIcon';

import { byPrefixAndName } from '@awesome.me/kit-d6dae216cd/icons';

const ActionsContainer = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    align-items: flex-end;
`;

const CheckBookIcon = styled(NwIcon)`
    font-size: 1.4rem;
`;

export function buildBookItemMenuActions({ book, isEmptyBook, hasCover, userPermission, canPublishBooks, actions }) {
    const tooltipText = (text) => (isEmptyBook ? 'empty book' : text);
    const favouriteLabel = book.IsFavourite ? 'remove from favourites' : 'add to favourites';

    const b_actions = [
        {
            name: '',
            key: 'edit',
            tooltip: 'edit book',
            pinned: true,
            icon: byPrefixAndName.fasl['pen'],
            action: () => actions.onEdit(book),
        },
        {
            name: '',
            key: 'preview',
            pinned: true,
            tooltip: tooltipText('view book'),
            icon: byPrefixAndName.fasl['images-user'],
            disabled: isEmptyBook,
            action: () => actions.onOpenBookPreviewDrawer(book),
        },
        {
            name: '',
            key: 'info',
            pinned: true,
            tooltip: 'info',
            icon: byPrefixAndName.fasl['circle-info'],
            action: () => actions.onOpenBookInfoDrawer(book),
        },
        {
            name: '',
            pinned: true,
            key: 'publish',
            tooltip: tooltipText('publish on website'),
            icon: byPrefixAndName.fasl['globe'],
            disabled: isEmptyBook || !canPublishBooks,
            action: () => actions.onOpenPublishDrawer(book),
        },
        {
            name: 'clone book',
            key: 'clone',
            tooltip: tooltipText('clone this book'),
            icon: byPrefixAndName.fasl['copy'],
            disabled: isEmptyBook,
            action: () => actions.onCloneBook(book),
        },
        {
            name: 'select cover',
            key: 'addcover',
            disabled: hasCover || isEmptyBook,
            tooltip: tooltipText('select and crop a cover image'),
            icon: byPrefixAndName.fasl['scissors'],
            action: () => actions.onOpenBookCoverDrawer(book),
        },
        {
            name: 'remove cover',
            key: 'removecover',
            tooltip: tooltipText('remove selected cover image'),
            disabled: isEmptyBook || !hasCover,
            icon: byPrefixAndName.fasl['xmark'],
            action: () => actions.onRemoveCover(book),
        },
        {
            name: 'delete',
            type: 'delete',
            key: 'delete',
            tooltip: 'delete book',
            icon: byPrefixAndName.fasl['trash-alt'],
            confirmationPlacement: 'left',
            disabled: !userPermission,
            action: () => actions.onDelete(book),
        },
    ];

    let fpaction;
    let compaction;
    if (book.FastPackage) {
        fpaction = {
            name: 'remove as fast package book',
            key: 'fast',
            tooltip: tooltipText('fast package'),
            disabled: isEmptyBook,
            icon: byPrefixAndName.fasl['truck-fast'],
            action: () => actions.onSetFastPackage(book, false),
        };
    } else {
        fpaction = {
            name: 'set as fast package book',
            key: 'fast',
            tooltip: tooltipText('fast package'),
            disabled: isEmptyBook,
            icon: byPrefixAndName.fasl['truck-fast'],
            action: () => actions.onSetFastPackage(book, true),
        };
    }
    if (book.CompCard) {
        compaction = {
            name: 'set as not compcard',
            key: 'comp',
            tooltip: tooltipText('not compcard'),
            disabled: isEmptyBook,
            icon: byPrefixAndName.fasl['image-polaroid-user'],
            action: () => actions.onSetCompCard(book, false),
        };
    } else {
        compaction = {
            name: 'set as compcard',
            key: 'comp',
            tooltip: tooltipText('compcard'),
            disabled: isEmptyBook,
            icon: byPrefixAndName.fasl['image-polaroid-user'],
            action: () => actions.onSetCompCard(book, true),
        };
    }
    const favaction = {
        name: favouriteLabel,
        key: 'favourite',
        tooltip: tooltipText(favouriteLabel),
        disabled: isEmptyBook,
        icon: book.IsFavourite ? byPrefixAndName.fasl['heart-circle-xmark'] : byPrefixAndName.fasl['heart-circle-plus'],
        action: () => actions.onSetFavourite(book, !book.IsFavourite),
    };
    b_actions.splice(2, 0, fpaction, compaction, favaction);

    return b_actions;
}

/** Azioni pinnate su mobile: le altre restano raggiungibili dal menu ellipsis. */
const MOBILE_PINNED_ACTION_KEYS = ['edit', 'publish'];

/** Le azioni pinnate sul desktop non hanno etichetta: serve quando scendono nel menu. */
const MOBILE_MENU_ACTION_NAMES = {
    preview: 'view book',
    info: 'book info',
};

/** Adatta la lista azioni allo spazio del mobile: meno bottoni visibili, gli altri nel menu. */
export function adaptBookItemMenuActionsForMobile(menuActions) {
    return menuActions.map((action) => {
        const pinned = MOBILE_PINNED_ACTION_KEYS.includes(action.key);
        return {
            ...action,
            pinned,
            name: pinned ? action.name : action.name || MOBILE_MENU_ACTION_NAMES[action.key] || action.key,
        };
    });
}

export function renderBookItemStandardActions({
    actionsHeight,
    book,
    isEmptyBook,
    hasCover,
    userPermission,
    canPublishBooks,
    actions,
    isMobile,
}) {
    const builtActions = buildBookItemMenuActions({
        book,
        isEmptyBook,
        hasCover,
        userPermission,
        canPublishBooks,
        actions,
    });
    const menuActions = isMobile ? adaptBookItemMenuActionsForMobile(builtActions) : builtActions;
    return [
        <ActionsContainer $height={actionsHeight} key="book-actions">
            <NwDropDownMenu actions={menuActions} />
        </ActionsContainer>,
    ];
}

export function renderBookItemFastPackageRemoveActions({
    actionsHeight,
    isEmptyBook,
    sortableNoDragProps,
    book,
    onRemoveFastPackage,
}) {
    return [
        <ActionsContainer $height={actionsHeight} key="fast-pack-remove">
            <NwLinkButton
                size="small"
                icon={byPrefixAndName.fasl['times']}
                label="remove as fast package book"
                disabled={isEmptyBook}
                {...sortableNoDragProps}
                onClick={(e) => {
                    e.stopPropagation();
                    onRemoveFastPackage(book);
                }}
            />
        </ActionsContainer>,
    ];
}

export function renderBookItemPackageSectionActions({ onPreview }) {
    return [
        <CheckBookIcon
            key="package-preview"
            icon={byPrefixAndName.fasl['magnifying-glass-plus']}
            pointer
            onClick={onPreview}
        />,
    ];
}
